import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell, { authInput } from "@/pages/AuthShell";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState("checking"); // checking | ready | exists | failed
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setSession } = useAuth();
  const nav = useNavigate();
  const attempts = useRef(0);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (!sessionId) { setPhase("failed"); return; }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setEmail(data.email || "");
          setPhase(data.account_exists ? "exists" : "ready");
          return;
        }
        if (data.status === "expired") { setPhase("failed"); return; }
      } catch {}
      attempts.current += 1;
      if (attempts.current >= 8) { setPhase("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  const createAccount = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/set-password", { session_id: sessionId, password });
      setSession(data);
      nav("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  if (phase === "checking") {
    return (
      <AuthShell eyebrow="Payment" title="Confirming your purchase">
        <div style={{ display: "flex", gap: 10, alignItems: "center", color: "var(--muted)" }} data-testid="checkout-checking">
          <Loader2 size={20} className="animate-spin" color="#2038E6" /> Verifying payment with Stripe...
        </div>
      </AuthShell>
    );
  }

  if (phase === "failed") {
    return (
      <AuthShell eyebrow="Payment" title="We could not confirm payment"
        footer={<Link to="/" style={{ color: "#2038E6" }}>Return home</Link>}>
        <p data-testid="checkout-failed" style={{ color: "var(--muted)" }}>
          Payment was not completed or the session expired. If you were charged, check your email for a receipt or contact support.
        </p>
      </AuthShell>
    );
  }

  if (phase === "exists") {
    return (
      <AuthShell eyebrow="Payment confirmed" title="Account already set up"
        footer={<Link to="/login" style={{ color: "#2038E6" }}>Go to login</Link>}>
        <p style={{ color: "var(--muted)" }} data-testid="checkout-exists">
          An account already exists for <strong>{email}</strong>. Please log in to access the portal.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="Payment confirmed" title="Create your account">
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 18, color: "#2038E6" }}>
        <CheckCircle2 size={20} /> <span className="font-mono-plex" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>Payment received · $97</span>
      </div>
      <form onSubmit={createAccount} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label className="eyebrow">Your email</label>
          <input data-testid="account-email" type="email" value={email} readOnly style={{ ...authInput, marginTop: 6, background: "#f2f3f0", color: "var(--muted)" }} />
        </div>
        <div>
          <label className="eyebrow">Set a password</label>
          <input data-testid="account-password" type="password" placeholder="Min 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...authInput, marginTop: 6 }} />
        </div>
        {error && <span style={{ color: "#b00020", fontSize: 14 }} data-testid="account-error">{error}</span>}
        <button className="btn-cobalt" type="submit" disabled={loading} data-testid="account-submit" style={{ justifyContent: "center" }}>
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Enter the portal"}
        </button>
      </form>
    </AuthShell>
  );
}
